import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, ShoppingCart, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

export default function PharmacyChat() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id || user?.id || user?.userId;
  const [conversationId, setConversationId] = useState(sp.get("conversationId") || null);
  const [pharmacyId, setPharmacyId] = useState(sp.get("pharmacyId") || null);
  const [pharmacyName, setPharmacyName] = useState(sp.get("pharmacyName") || "Pharmacy");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadMessages = useCallback(async (cid) => {
    try {
      const res = await api.get(`/pharmacy-chat/${cid}/messages`);
      if (res.data?.success) setMessages(res.data.messages || []);
    } catch { /* ignore */ }
  }, []);

  const markRead = useCallback(async (cid) => {
    try { await api.post(`/pharmacy-chat/${cid}/read`, { reader: "patient" }); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      let cid = sp.get("conversationId");
      try {
        if (!cid && pharmacyId && userId) {
          const res = await api.post(`/pharmacy-chat/start`, { patient_id: userId, pharmacy_id: pharmacyId });
          if (res.data?.success) {
            cid = res.data.conversation.id;
            setPharmacyName(res.data.conversation.pharmacyName || pharmacyName);
            setPharmacyId(res.data.conversation.pharmacyId);
          }
        }
        if (cid) {
          setConversationId(cid);
          await loadMessages(cid);
          markRead(cid);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [pharmacyId, userId]);

  // Poll for new messages (web has no WS client) every 4s
  useEffect(() => {
    if (!conversationId) return;
    const t = setInterval(() => { loadMessages(conversationId); markRead(conversationId); }, 4000);
    return () => clearInterval(t);
  }, [conversationId, loadMessages, markRead]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if (!body || !conversationId || sending) return;
    setSending(true);
    setText("");
    try {
      const res = await api.post(`/pharmacy-chat/${conversationId}/messages`, { sender_type: "patient", sender_id: userId, text: body });
      if (res.data?.success) setMessages((prev) => (prev.some((m) => m.id === res.data.message.id) ? prev : [...prev, res.data.message]));
    } catch {
      setText(body);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto flex flex-col" style={{ height: "calc(100vh - 2rem)" }}>
      <div className="flex items-center gap-3 py-3 px-2 border-b bg-background sticky top-0 z-10">
        <button onClick={() => navigate(-1)} data-testid="chat-back" className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent"><ArrowLeft className="h-5 w-5" /></button>
        <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "#E0F2F1" }}><Stethoscope className="h-4 w-4" style={{ color: "#005C66" }} /></div>
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{pharmacyName}</p>
          <p className="text-xs text-muted-foreground">Enquiry chat</p>
        </div>
        <Button data-testid="chat-place-order" onClick={() => navigate(`/place-order?pharmacyId=${pharmacyId}&pharmacyName=${encodeURIComponent(pharmacyName)}&conversationId=${conversationId || ""}`)} style={{ backgroundColor: "#005C66" }} className="rounded-xl">
          <ShoppingCart className="h-4 w-4 mr-1.5" /> Order
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <p className="text-center text-muted-foreground mt-10">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-muted-foreground mt-10 px-8">Ask {pharmacyName} about medicine availability, price or delivery. Replies appear here.</p>
        ) : (
          messages.map((m) => {
            const mine = m.senderType === "patient";
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm ${mine ? "text-white rounded-br-sm" : "bg-white border rounded-bl-sm"}`} style={mine ? { backgroundColor: "#005C66" } : {}}>
                  {m.text}
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="flex items-end gap-2 p-3 border-t bg-background">
        <textarea
          data-testid="chat-input"
          className="flex-1 resize-none bg-muted rounded-2xl px-4 py-2.5 text-sm max-h-28 outline-none"
          placeholder="Type your enquiry…"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <Button data-testid="chat-send" onClick={send} disabled={!text.trim() || sending} size="icon" className="rounded-full h-11 w-11" style={{ backgroundColor: "#005C66" }}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
